import dayjs from 'dayjs';
import { useMemo } from 'react';
import Modal from 'react-bootstrap/Modal';
import { useQuery } from '@tanstack/react-query';
import { formatDateToString } from '../../../helpers/formatDateToString';
import { formatPriceToBRL } from '../../../helpers/formatPriceToBRL';
import { type AttachmentDto } from '../../../models/AttachmentDto';
import { type PaymentDto } from '../../../models/PaymentDto';
import { getManagerInstallmentDetails } from '../core/_requests';

type Props = {
  show: boolean;
  installmentId: string;
  onClose: () => void;
};

const InstallmentDetailsModal = ({ show, installmentId, onClose }: Props) => {
  const { data: response, isPending } = useQuery({
    queryKey: ['manager-installment-details', installmentId],
    queryFn: () => getManagerInstallmentDetails(installmentId),
    enabled: !!installmentId && show,
  });

  const installment = response?.data;

  const paymentStats = useMemo(() => {
    const grossValue = installment?.gross_value ?? 0;
    const amountPaid = installment?.amount_paid ?? 0;
    const amountUnpaid = installment?.amount_unpaid ?? 0;
    const payments = installment?.payments ?? [];
    const percentage = grossValue > 0 ? Math.min(100, Math.round((amountPaid / grossValue) * 100)) : 0;

    return { grossValue, amountPaid, amountUnpaid, paymentCount: payments.length, percentage };
  }, [installment]);

  const lastPaymentDate = useMemo(() => {
    const datedPayments = (installment?.payments ?? []).filter((p) => !!p.registration_date);
    if (!datedPayments.length) return 'Nunca';
    const latest = datedPayments.reduce(
      (acc, cur) => (dayjs(cur.registration_date).isAfter(acc) ? cur.registration_date! : acc),
      datedPayments[0].registration_date!,
    );
    return formatDateToString(latest ?? '');
  }, [installment?.payments]);

  const renderStatusBadge = () => {
    const value = installment?.status?.value;
    switch (value) {
      case 'OPENED':
        return <span className="badge badge-light-info">Em aberto</span>;
      case 'PAID':
        return <span className="badge badge-light-success">Paga</span>;
      case 'DELAYED':
        return <span className="badge badge-light-danger">Atrasada</span>;
      case 'CANCELED':
        return <span className="badge badge-light-secondary">Cancelada</span>;
      default:
        return <span className="badge badge-light-warning">{installment?.status?.description ?? value ?? '--'}</span>;
    }
  };

  const renderProgressBarColor = () => {
    const status = installment?.status?.value;
    if (status === 'PAID') return 'bg-success';
    if (status === 'CANCELED') return 'bg-secondary';
    if (status === 'DELAYED') return 'bg-danger';
    if (paymentStats.percentage > 0) return 'bg-info';
    return 'bg-primary';
  };

  const renderPaymentRow = (payment: PaymentDto, index: number) => {
    const interest = payment.interest ?? 0;
    const fine = payment.fine ?? 0;
    const discount = payment.discount ?? 0;
    const hasAdjustments = interest > 0 || fine > 0 || discount > 0;

    return (
      <tr key={payment.id ?? index}>
        <td className="text-gray-800 fw-semibold">{formatDateToString(payment.date ?? '')}</td>
        <td>
          <span className="badge badge-light-primary">{payment.payment_method?.description ?? '--'}</span>
        </td>
        <td className="text-gray-700 fw-semibold">{formatPriceToBRL(payment.gross_amount ?? 0)}</td>
        <td>
          {hasAdjustments ? (
            <div className="d-flex flex-column gap-1">
              {interest > 0 && <span className="text-danger fs-8">Juros: {formatPriceToBRL(interest)}</span>}
              {fine > 0 && <span className="text-danger fs-8">Multa: {formatPriceToBRL(fine)}</span>}
              {discount > 0 && <span className="text-success fs-8">Desconto: -{formatPriceToBRL(discount)}</span>}
            </div>
          ) : (
            <span className="text-gray-400 fs-8">--</span>
          )}
        </td>
        <td className="text-gray-900 fw-bold">{formatPriceToBRL(payment.net_amount ?? 0)}</td>
      </tr>
    );
  };

  const formatFileSize = (sizeInBytes: number) => {
    if (sizeInBytes >= 1_000_000) return `${(sizeInBytes / 1_000_000).toFixed(2)} MB`;
    return `${(sizeInBytes / 1_000).toFixed(0)} KB`;
  };

  const renderAttachmentRow = (attachment: AttachmentDto, index: number) => (
    <tr key={attachment.id ?? index}>
      <td className="text-gray-700">{attachment.name ?? '--'}</td>
      <td className="text-gray-600">{formatFileSize(attachment.size)}</td>
      <td className="text-gray-600 text-uppercase">{attachment.extension ?? '--'}</td>
      <td>
        {attachment.public_url ? (
          <a
            className="btn btn-sm btn-light-primary"
            href={attachment.public_url}
            rel="noopener noreferrer"
            target="_blank"
          >
            Baixar
          </a>
        ) : (
          <span className="text-gray-400">--</span>
        )}
      </td>
    </tr>
  );

  const payments = installment?.payments ?? [];
  const attachments = installment?.attachments ?? [];
  const billAttachments = installment?.bill?.attachments ?? [];
  const allAttachments = [...attachments, ...billAttachments].filter(
    (att, i, arr) => arr.findIndex((a) => a.id === att.id) === i,
  );

  return (
    <Modal show={show} size="xl" onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-3">
          <span>{installment?.bill?.description ?? installment?.description ?? 'Detalhes da Parcela'}</span>
          {!!installment && renderStatusBadge()}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isPending ? (
          <div className="d-flex justify-content-center py-10">
            <span className="spinner-border" role="status" />
          </div>
        ) : !installment ? (
          <div className="alert alert-warning">Não foi possível carregar os detalhes.</div>
        ) : (
          <>
            {/* Info Cards */}
            <div className="row g-5 mb-6">
              {/* Installment Info */}
              <div className="col-12 col-md-6">
                <div className="border border-gray-300 rounded p-5">
                  <h5 className="fw-bold text-gray-800 mb-4">Informações da Parcela</h5>
                  <div className="d-flex justify-content-between mb-3">
                    <span className="text-gray-600">Parcela</span>
                    <span className="fw-bold text-gray-800">
                      {installment.idx}/{installment.bill?.installment_quantity ?? '?'}
                    </span>
                  </div>
                  <div className="separator my-2" />
                  <div className="d-flex justify-content-between mb-3">
                    <span className="text-gray-600">Vencimento</span>
                    <span className="fw-bold text-gray-800">{formatDateToString(installment.due_date ?? '')}</span>
                  </div>
                  <div className="separator my-2" />
                  <div className="d-flex justify-content-between mb-3">
                    <span className="text-gray-600">Contato</span>
                    <span className="fw-bold text-gray-800">{installment.bill?.contact?.name ?? '--'}</span>
                  </div>
                  <div className="separator my-2" />
                  <div className="d-flex justify-content-between mb-3">
                    <span className="text-gray-600">Categoria</span>
                    <span className="fw-bold text-gray-800">{installment.bill?.category?.name ?? '--'}</span>
                  </div>
                  <div className="separator my-2" />
                  <div className="d-flex justify-content-between">
                    <span className="text-gray-600">Valor da parcela</span>
                    <span className="fw-bold text-gray-800">{formatPriceToBRL(installment.gross_value ?? 0)}</span>
                  </div>
                </div>
              </div>

              {/* Payment Summary */}
              <div className="col-12 col-md-6">
                <div className="border border-gray-300 rounded p-5">
                  <h5 className="fw-bold text-gray-800 mb-4">Resumo de Pagamento</h5>

                  {/* Progress bar */}
                  <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="text-gray-600 fs-7">Progresso</span>
                      <span className="text-gray-800 fw-bold">{paymentStats.percentage}%</span>
                    </div>
                    <div className="h-8px w-100 bg-light rounded" style={{ overflow: 'hidden' }}>
                      <div
                        className={`h-100 rounded ${renderProgressBarColor()}`}
                        style={{ width: `${paymentStats.percentage}%`, transition: 'width 0.5s ease-in-out' }}
                      />
                    </div>
                  </div>

                  <div className="d-flex justify-content-between mb-3">
                    <span className="text-gray-600">Valor pago</span>
                    <span className="fw-bold text-success">{formatPriceToBRL(paymentStats.amountPaid)}</span>
                  </div>
                  <div className="separator my-2" />
                  <div className="d-flex justify-content-between mb-3">
                    <span className="text-gray-600">Valor em aberto</span>
                    <span className={`fw-bold ${paymentStats.amountUnpaid > 0 ? 'text-warning' : 'text-gray-700'}`}>
                      {formatPriceToBRL(Math.max(0, paymentStats.amountUnpaid))}
                    </span>
                  </div>
                  <div className="separator my-2" />
                  <div className="d-flex justify-content-between mb-3">
                    <span className="text-gray-600">Pagamentos realizados</span>
                    <span className="fw-bold text-gray-800">{paymentStats.paymentCount}</span>
                  </div>
                  <div className="separator my-2" />
                  <div className="d-flex justify-content-between">
                    <span className="text-gray-600">Último pagamento</span>
                    <span className="fw-bold text-gray-800">{lastPaymentDate}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs: Payments & Attachments */}
            <ul className="nav nav-tabs nav-line-tabs fs-6 mb-5">
              <li className="nav-item">
                <a className="nav-link active fw-bold" data-bs-toggle="tab" href="#detailPaymentsTab">
                  Pagamentos ({payments.length})
                </a>
              </li>
              <li className="nav-item">
                <a className="nav-link fw-bold" data-bs-toggle="tab" href="#detailAttachmentsTab">
                  Anexos ({allAttachments.length})
                </a>
              </li>
            </ul>

            <div className="tab-content">
              {/* Payments Tab */}
              <div className="tab-pane fade show active" id="detailPaymentsTab" role="tabpanel">
                {payments.length === 0 ? (
                  <div className="text-center text-gray-500 py-6">Nenhum pagamento registrado.</div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
                      <thead>
                        <tr className="fw-bold text-muted">
                          <th>Data</th>
                          <th>Forma</th>
                          <th>Valor Base</th>
                          <th>Ajustes</th>
                          <th>Total Pago</th>
                        </tr>
                      </thead>
                      <tbody>{payments.map(renderPaymentRow)}</tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Attachments Tab */}
              <div className="tab-pane fade" id="detailAttachmentsTab" role="tabpanel">
                {allAttachments.length === 0 ? (
                  <div className="text-center text-gray-500 py-6">Nenhum anexo disponível.</div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
                      <thead>
                        <tr className="fw-bold text-muted">
                          <th>Nome</th>
                          <th>Tamanho</th>
                          <th>Tipo</th>
                          <th>Ação</th>
                        </tr>
                      </thead>
                      <tbody>{allAttachments.map(renderAttachmentRow)}</tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-secondary" type="button" onClick={onClose}>
          Fechar
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default InstallmentDetailsModal;
